import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileUp, CheckCircle2, AlertCircle } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import Modal from "@/components/app/Modal";
import { btnOutline, btnPrimary } from "@/components/app/ui";

// Shared CSV import dialog: download a template, pick a file, preview (dry run) with every row error, then import.
// `kind` is "products" or "inventory"; `summary(result)` returns the chips shown after a check / import.
export default function CsvImportModal({ open, onClose, onDone, title, subtitle, templateUrl, templateLabel, fileName, importUrl, hint, summary, columns }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const input = useRef(null);

  useEffect(() => { if (open) { setFile(null); setResult(null); setError(""); } }, [open]);

  const download = async () => {
    try {
      const { data } = await api.get(templateUrl, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = fileName || "template.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) { toast.error("Could not download the file."); }
  };

  const run = async (dry) => {
    if (!file) return;
    setBusy(true); setError("");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("dry_run", dry ? "true" : "false");
    try {
      const { data } = await api.post(importUrl, fd);
      setResult(data);
      if (!dry) {
        toast.success("Import finished.");
        onDone?.();
        onClose();
      }
    } catch (err) { setResult(null); setError(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pick = (f) => {
    setResult(null); setError("");
    if (f && !/\.(csv|txt)$/i.test(f.name)) { setFile(null); setError("Please choose a .csv file."); return; }
    setFile(f || null);
  };
  const applicable = result ? summary(result).filter((c) => c.tone !== "bad").reduce((n, c) => n + (c.count || 0), 0) : 0;

  return (
    <Modal open={open} onClose={onClose} title={title} subtitle={subtitle} size="lg" testId="csv-import-modal"
      footer={<>
        <button onClick={onClose} className={btnOutline}>Cancel</button>
        <button onClick={() => run(true)} disabled={!file || busy} data-testid="csv-check-btn" className={`${btnOutline} disabled:opacity-50`}>{busy && !result ? "Checking…" : "Check file"}</button>
        <button onClick={() => run(false)} disabled={!file || busy || !result || applicable === 0} data-testid="csv-import-btn" className={`${btnPrimary} disabled:opacity-50`}>{busy && result ? "Importing…" : "Import"}</button>
      </>}>
      <div className="space-y-4 text-[13px]">
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-3 space-y-1.5">
          <div className="font-semibold text-neutral-900">1. Get the template</div>
          <p className="text-slate-600">{hint}</p>
          <p className="text-slate-500 text-[12px]">Columns: <span className="font-mono">{columns}</span></p>
          <button onClick={download} data-testid="csv-template-btn" className={`${btnOutline} !h-8 !text-[12px] mt-1`}><Download size={13} /> {templateLabel}</button>
        </div>

        <div>
          <div className="font-semibold text-neutral-900 mb-1.5">2. Upload your CSV</div>
          <input ref={input} type="file" accept=".csv,text/csv" className="hidden" data-testid="csv-file-input" onChange={(e) => pick(e.target.files?.[0])} />
          <button onClick={() => input.current?.click()} className="w-full rounded-lg border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/40 py-5 flex flex-col items-center gap-1 text-slate-600">
            <FileUp size={20} className="text-blue-600" />
            <span className="font-medium">{file ? file.name : "Choose a CSV file"}</span>
            {file && <span className="text-[11px] text-slate-400">{(file.size / 1024).toFixed(1)} KB · click to change</span>}
          </button>
        </div>

        {error && <div role="alert" data-testid="csv-error" className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2 flex gap-2"><AlertCircle size={15} className="shrink-0 mt-0.5" />{error}</div>}

        {result && (
          <div className="space-y-2" data-testid="csv-result">
            <div className="font-semibold text-neutral-900">3. Review</div>
            <div className="flex flex-wrap gap-2">
              {summary(result).map((c) => (
                <span key={c.label} className={`px-2.5 py-1 rounded-md border text-[12px] font-medium ${c.tone === "bad" ? "bg-red-50 text-red-700 border-red-200" : "bg-green-50 text-green-700 border-green-200"}`}>{c.count} {c.label}</span>
              ))}
            </div>
            {result.changes?.length > 0 && (
              <div className="max-h-44 overflow-y-auto rounded-lg border border-slate-200">
                <table className="w-full text-[12px]"><thead className="bg-slate-50 text-slate-500 sticky top-0"><tr><th className="text-left px-2.5 py-1.5">SKU</th><th className="text-left px-2.5">Item</th><th className="text-right px-2.5">Stock</th></tr></thead>
                  <tbody>{result.changes.map((c, i) => (
                    <tr key={i} className="border-t border-slate-100"><td className="px-2.5 py-1 font-mono">{c.sku}</td><td className="px-2.5">{c.name}</td>
                      <td className="px-2.5 text-right whitespace-nowrap">{c.before} → <b className={c.after > c.before ? "text-green-600" : "text-red-600"}>{c.after}</b></td></tr>
                  ))}</tbody></table>
              </div>
            )}
            {result.new_categories?.length > 0 && (
              <p className="text-[12px] text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-2.5 py-1.5" data-testid="csv-new-categories">New customer categories will be created: <b>{result.new_categories.join(", ")}</b></p>
            )}
            {result.errors?.length > 0 && (
              <div className="max-h-40 overflow-y-auto rounded-lg border border-red-200 bg-red-50/50 divide-y divide-red-100" data-testid="csv-errors">
                {result.errors.map((e, i) => <div key={i} className="px-2.5 py-1.5 text-[12px] text-red-700"><b>Row {e.row}:</b> {e.message}</div>)}
              </div>
            )}
            {result.failed === 0 && applicable > 0 && <p className="text-green-700 flex items-center gap-1.5 text-[12px]"><CheckCircle2 size={14} /> Looks good — click Import to apply.</p>}
            {result.failed > 0 && applicable > 0 && <p className="text-slate-500 text-[12px]">Rows with errors are skipped; the valid ones will be imported.</p>}
          </div>
        )}
      </div>
    </Modal>
  );
}
